<template>
  <div class="dashboard-content">
    <div class="dashboard-details-container">
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-for="(buildStep, index) in dashboardInformation.buildSteps"
        :key="index"
        class="dashboard-details-card"
      >
        <div class="header">
          {{ buildStep.buildStepName }}

          <div
            class="count"
            :class="countClass(buildStep.orders.length)"
          >
            {{ buildStep.orders.length }}
          </div>
        </div>
        <div class="orders">
          <div
            v-for="order in buildStep.orders"
            :key="order.orderId"
            class="order"
          >
            <div
              v-if="order.onHold" 
              class="tag"
            >
              On Hold
            </div>
            <div class="name">
              {{ order.customerName }} - Order #{{ order.orderId }}
            </div>
            <div v-if="order.scheduledDate">
              Scheduled Date: {{ $methods.formatDate(order.scheduledDate) }}
            </div>
            <div v-else>
              Scheduled Date: Order not yet scheduled
            </div>
            <div v-if="order.cartridgeName && order.cartridgeName !== ''">
              {{ `${order.buildTypeName} - ${order.cartridgeName}` }}
            </div>
            <div v-else>
              {{ order.buildTypeName }}
            </div>
            <div v-if="order.isStarted">
              {{ `${Math.round(order.daysInStep)} days in build step` }}
            </div>
            <div v-else>
              {{ `${Math.round(order.daysWaitingToBeStarted)} days waiting for build step to be started` }}
            </div>
            <div>{{ `${Math.round(order.daysInShop)} days in shop` }}</div>
            <div class="actions">
              <button
                class="view-order"
                @click="onOrderClicked(order.orderId)"
              >
                View Order
              </button>
              <button
                v-if="!order.showNotes && order.notes.length > 0"
                @click="order.showNotes = true"
              >
                View Notes
              </button>
              <button
                v-if="order.showNotes"
                @click="order.showNotes = false"
              >
                Hide Notes
              </button>
              <button
                class="add-note"
                @click="onAddNoteClicked(order.orderId)"
              >
                Add Note
              </button>
            </div>
            <div v-if="order.showNotes">
              <NoteTile
                v-for="orderNote in order.notes"
                :key="orderNote.id"
                :note="orderNote"
                @delete="onDeleteNote"
                @edit="onEditNoteClicked(orderNote, order.orderId)"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!loading && dashboardInformation.customerApprovedQuotes"
        class="dashboard-details-card"
      >
        <div class="header">
          Customer Approved Quotes

          <div
            class="count"
            :class="countClass(dashboardInformation.customerApprovedQuotes.length)"
          >
            {{ dashboardInformation.customerApprovedQuotes.length }}
          </div>
        </div>
        <div class="orders">
          <div
            v-for="quote in dashboardInformation.customerApprovedQuotes"
            :key="quote.quoteId"
            class="order"
            @click="onQuoteClicked(quote.quoteId)"
          >
            <div class="name">
              {{ quote.customerName }} - Quote #{{ quote.quoteId }}
            </div>
            <div>
              Approval Date: {{ $methods.formatDate(quote.approvedDate) }}
            </div>
            <div v-if="quote.cartridgeName && quote.cartridgeName !== ''">
              {{ `${quote.buildTypeName} - ${quote.cartridgeName}` }}
            </div>
            <div v-else>
              {{ quote.buildTypeName }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!loading && dashboardInformation.purchaseOrders"
        class="dashboard-details-card"
      >
        <div class="header">
          P.O.'s Needing Verified

          <div
            class="count"
            :class="countClass(dashboardInformation.purchaseOrders.length)"
          >
            {{ dashboardInformation.purchaseOrders.length }}
          </div>
        </div>
        <div class="orders">
          <router-link
            v-for="purchaseOrder in dashboardInformation.purchaseOrders"
            :key="purchaseOrder.purchaseOrderId"
            class="order"
            :to="'/purchase-order/' + purchaseOrder.purchaseOrderId"
          >
            {{ purchaseOrder.poNumber }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
  <transition name="slide-up">
    <NoteModal 
      v-if="note"
      :note="note"
      :order-id="noteOrderId"
      @note-changed="getDashboardInformation"
      @close="note = undefined"
    />
  </transition>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { DashboardInformationService } from '@/services/dashboardInformation.service'
import { DashboardInformationDetails } from '@/@types/responses/dashboard_information/DashboardInformationDetails'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import NoteTile from '@/components/elements/NoteTile.vue'
import { Note } from '@/@types/responses/note/Note'
import { NoteService } from '@/services/note.service'
import NoteModal from '@/components/modals/NoteModal.vue'

export default defineComponent({
  name: 'Dashboard',
  components: {
    LoadingPulse,
    NoteModal,
    NoteTile
  },
  beforeRouteLeave() {
    if (this.note) {
      this.note = undefined
      return false
    }

    return true
  },
  setup() {
    const dashboardInformationService = inject(PROVIDER_CONSTANTS.dashboardInformationService) as DashboardInformationService
    const noteService = inject(PROVIDER_CONSTANTS.noteService) as NoteService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      dashboardInformationService,
      noteService,
      toast
    }
  },
  data() {
    let note: Note|undefined

    return {
      dashboardInformation: new DashboardInformationDetails({ buildSteps: [], customerApprovedQuotes: [], purchaseOrders: [] }),
      loading: true,
      note,
      noteOrderId: 0
    }
  },
  mounted() {
    this.getDashboardInformation()
  },
  methods: {
    countClass(count: number): string {
      if (count < 5) return 'good'
      else if (count < 10) return 'warn'

      return 'bad'
    },
    getDashboardInformation(): Promise<void> {
      return this.dashboardInformationService.getDashboardInformation().then(dashboardInformation => {
        this.dashboardInformation = dashboardInformation
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the dashboard information. Try reloading the page.')
        }
      })
    },
    onAddNoteClicked(orderId: number): void {
      this.noteOrderId = orderId
      this.note = new Note(0)
    },
    onDeleteNote(note: Note): void {
      this.noteService.deleteNote(note.id).then(() => {
        this.getDashboardInformation().then(() => {
          this.toast.success('The note was deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the note. Please try again.')
        }
      })
    },
    onEditNoteClicked(note: Note, orderId: number): void {
      this.noteOrderId = orderId
      this.note = note
    },
    onOrderClicked(id: number): void {
      this.$router.push('/order/' + id)
    },
    onQuoteClicked(id: number): void {
      this.$router.push('/quote/' + id)
    }
  }
})
</script>

<style scoped lang="scss">
.dashboard-content {
  background-color: $Color-fill-gray;
  padding: 15px;
  min-height: calc(100vh - 94px);

  @include desktop {
    min-height: calc(100vh - 130px);
  }

  > .dashboard-details-container {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    position: relative;
    margin-left: -15px;

    @include desktop {
      flex-direction: row;
      margin-left: -30px;
    }

    > .loading-spinner {
      margin: auto;
    }

    > .dashboard-details-card {
      width: 100%;
      padding: 0 0 15px 15px;

      @include desktop {
        width: calc(25% - 30px);
        max-height: 300px;
        padding: 15px;
        margin-left: 30px;
        margin-bottom: 30px;
        border-radius: 2px;
        background-color: $Color-white;
        @include element-shadow;
      }

      > .header {
        font-weight: 600;
        font-size: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid lightgray;
        position: relative;

        > .count {
          position: absolute;
          top: 0;
          right: 0;

          &.good {
            color: green;
          }

          &.warn {
            color: $Color-sac-orange;
          }

          &.bad {
            color: $Color-font-error;
          }
        }
      }

      > .orders {
        @include desktop {
          max-height: 223px;
          overflow-y: auto;
        }

        > .order {
          display: block;
          text-decoration: none;
          padding: 10px;
          font-size: 12px;
          line-height: 20px;
          border-bottom: 1px solid lightgray;
          color: $Color-font-high;
          background-color: $Color-white;

          > .tag {
            display: inline-block;
            padding: 5px;
            background-color: red;
            color: white;
          }

          > .name {
            font-weight: 600;
            font-size: 14px;
          }

          > .actions {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));

            > button {
              @include flat-button;

              &.view-order {
                text-align: left;
                padding-left: 0;
              }

              &.add-note {
                text-align: right;
                padding-right: 0;
              }
            }
          }
        }
      }
    }
  }
}

.slide-up {
  @include modal-animation;
}
</style>
